import type {
  DatasetColumn,
  DatasetManifestEntry,
  ReportDefinition,
} from '../types/reporting';

// A related-column reference is "<datasetKey>.<column>" (dataset keys and
// column names never contain dots); primary references stay bare column names.
export interface SplitRef {
  dataset: string | null;
  column: string;
}

export function splitRef(ref: string): SplitRef {
  const dot = ref.indexOf('.');
  if (dot === -1) return { dataset: null, column: ref };
  return { dataset: ref.slice(0, dot), column: ref.slice(dot + 1) };
}

export type RelatedManifests = ReadonlyMap<string, DatasetManifestEntry>;

export const NO_RELATED: RelatedManifests = new Map();

// The declared many-to-one targets of `primary` the caller can actually access
// (present in the permission-filtered manifest). Every join-aware
// call site shares this map — picker options, compile, validation, and the
// runner's extract loads all agree on which datasets are joinable.
export function relatedDatasetsFor(
  primary: DatasetManifestEntry | null,
  manifest: { datasets: DatasetManifestEntry[] } | null
): Map<string, DatasetManifestEntry> {
  const map = new Map<string, DatasetManifestEntry>();
  if (!primary || !manifest) return map;
  for (const rel of primary.relationships ?? []) {
    if (rel.cardinality !== 'many_to_one') continue;
    const entry = manifest.datasets.find(d => d.key === rel.target);
    if (entry) map.set(rel.target, entry);
  }
  return map;
}

// Related dataset keys the definition references. Sort is excluded: the
// compiler drops sorts on non-emitted aliases, so a sort alone can never pull
// joined data.
export function referencedRelatedKeys(def: ReportDefinition): Set<string> {
  const keys = new Set<string>();
  const add = (ref: string | undefined | null): void => {
    if (!ref) return;
    const { dataset } = splitRef(ref);
    if (dataset !== null) keys.add(dataset);
  };
  def.columns.forEach(add);
  def.group_bys.forEach(g => add(g.column));
  if (def.pivot_column) add(def.pivot_column.column);
  def.filters.forEach(f => add(f.column));
  def.measures.forEach(m => add(m.column));
  // DEFENSIVE. For every legitimately saved definition this changes nothing:
  // the backend's semantic check forces a validated chart's refs to be members
  // of `def.columns`, which the loop above already collects. Included so the
  // collector stays self-contained (and the stamps stay correct) if that
  // chart-refs-⊆-columns invariant is ever relaxed.
  if (def.chart) {
    add(def.chart.group_by);
    add(def.chart.column);
  }
  return keys;
}

export function resolveColumnMeta(
  ref: string,
  primary: DatasetManifestEntry,
  related: RelatedManifests
): DatasetColumn | undefined {
  const { dataset, column } = splitRef(ref);
  const entry = dataset === null ? primary : related.get(dataset);
  return entry?.columns.find(c => c.name === column);
}

// "Episodes · SOC date" for related refs; the primary column's label otherwise.
export function refLabel(
  ref: string,
  primary: DatasetManifestEntry,
  related: RelatedManifests
): string {
  const { dataset, column } = splitRef(ref);
  const entry = dataset === null ? primary : related.get(dataset);
  const columnLabel =
    entry?.columns.find(c => c.name === column)?.label ?? column;
  return dataset === null || !entry
    ? columnLabel
    : `${entry.label} · ${columnLabel}`;
}

// Normalize the definition's version stamps from what it actually references:
// v3 is reserved for definitions carrying detail_columns_mode; otherwise,
// schema_version is v2 iff any dotted ref remains, and v1 for primary-only
// definitions. related_versions contains one entry per referenced related
// dataset in either joined version. The backend enforces these stamps as a
// contract (422 on mismatch — the stale-client-save guard and the
// trustworthy-stamp guarantee). Called at open and at save so neither stamp
// goes stale or lingers after the last related column is removed.
export function stampDefinitionVersions(
  def: ReportDefinition,
  related: RelatedManifests
): ReportDefinition {
  const referenced = [...referencedRelatedKeys(def)].sort();
  const schemaVersion: 1 | 2 | 3 =
    def.detail_columns_mode !== undefined ? 3 : referenced.length > 0 ? 2 : 1;
  const entries = referenced.flatMap(key => {
    const entry = related.get(key);
    return entry ? [[key, entry.version] as const] : [];
  });
  if (entries.length === 0) {
    const rest = { ...def, schema_version: schemaVersion };
    delete rest.related_versions;
    return rest;
  }
  return {
    ...def,
    schema_version: schemaVersion,
    related_versions: Object.fromEntries(entries),
  };
}
